// Token Management Utilities
// Extracted from BattleUI.js for reusability across the app
import { API_URL } from '../config/apiConfig';

/**
 * Get token expiry time from JWT
 * @param {string} token - JWT token
 * @returns {number} - Expiry time in milliseconds
 */
export function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error('Error parsing token:', error);
    return Date.now() + (15 * 60 * 1000); // Default to 15 minutes from now
  }
}

/**
 * Check if token needs refresh (less than 5 minutes left)
 * @param {string} token - JWT token
 * @returns {boolean} - True if token needs refresh
 */
export function needsTokenRefresh(token) {
  const expiry = getTokenExpiry(token);
  const timeUntilExpiry = expiry - Date.now();
  return timeUntilExpiry < (5 * 60 * 1000); // 5 minutes
}

/**
 * Refresh token function
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<string>} - New access token
 */
export async function refreshToken(refreshToken) {
  try {
    const response = await fetch(`${API_URL}/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    return data.token; // Return new access token
  } catch (error) {
    console.error('Token refresh error:', error);
    throw error;
  }
}

/**
 * Check if token is valid (not expired)
 * @param {string} token - JWT token
 * @returns {boolean} - True if token is valid
 */
export function isTokenValid(token) {
  if (!token) return false;
  const expiry = getTokenExpiry(token);
  return Date.now() < expiry;
}

/**
 * Get time until token expires in minutes
 * @param {string} token - JWT token
 * @returns {number} - Minutes until expiry
 */
export function getTimeUntilExpiry(token) {
  const expiry = getTokenExpiry(token);
  const timeUntilExpiry = expiry - Date.now();
  return Math.max(0, Math.ceil(timeUntilExpiry / (60 * 1000))); // Convert to minutes
}
